import React, { useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  IconButton,
  Zoom,
  Grid,
  Typography,
  Tooltip,
  Icon,
  Divider,
  ClickAwayListener,
} from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import PatientSummaryTooltip from '../Summary/Patientdetails/PatientSummaryTooltip';
import InfoIcon from '@material-ui/icons/InfoOutlined';
import NavigateBeforeIcon from '@material-ui/icons/NavigateBefore';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import RepeatIcon from '@material-ui/icons/Repeat';
import TouchAppIcon from '@material-ui/icons/TouchApp';
import GetAppSharpIcon from '@material-ui/icons/GetAppSharp';
import { movePriorExaminations, setSnackbar } from '../../../../store';

const useStyles = makeStyles((theme) => ({
  pat_name_title: {
    letterSpacing: '0.1rem',
    textTransform: 'uppercase',
    fontSize: theme.fonts.responsiveBig,
  },

  pat_name_title_hovered: {
    letterSpacing: '0.1rem',
    textTransform: 'uppercase',
    fontSize: theme.fonts.responsiveBig,
    color: theme.palette.primary.main,
    textShadow: 'rgb(41 41 41) 1px 1px 7px;',
    textDecoration: 'underline',
  },

  pat_name_title_container: {
    maxWidth: '95%',
  },

  deleteTooltip: {
    fontSize: theme.fonts.responsiveMedium,
  },

  subheader: {
    letterSpacing: '0.1rem',
    textTransform: 'uppercase',
    color: theme.palette.texts.secondary,
    fontSize: '0.5em',
  },
  arrow: {
    color: theme.palette.texts.secondary,
  },

  arrow_light: {
    color: theme.palette.texts.helper,
  },

  avatar: {
    color: theme.palette.secondary.light,
    background: theme.palette.primary.main,
    cursor: 'pointer',
  },
  patient_info_icon: {
    height: '20px',
    width: 'auto',
    marginLeft: '6px',
  },

  next_icon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '28px',
    width: '28px',
  },

  tooltip_texts: {
    letterSpacing: '0.1rem',
    fontSize: theme.fonts.responsiveMedium,
    display: 'flex',
    alignItems: 'center',
  },

  hoverEffect: {
    width: '22px',
    padding: '0',
    marginRight: '6px',
    '&:hover': {
      background: `${theme.palette.accents.light} !important`,
      boxShadow: '0px 0px 5px 0px #d7c2c2',
    },
  },
}));

export default function ResultDialogTitle(props) {
  const classes = useStyles();
  const results = useSelector((state) => state.results);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { totalExaminations } = useSelector((state) => state.examinations);
  const { zoomedProjection } = useSelector((state) => state.gui);
  const [patientInfoHover, setPatientInfoHover] = useState(false);
  const [patientInfoPinned, setPatientInfoPinned] = useState(false);
  const [priorHover, setPriorHover] = useState(false);

  const patientInfoCloseTimer = useRef(null);
  const patientInfoOpen = patientInfoPinned || patientInfoHover;

  const handlePatientInfoMouseEnter = () => {
    clearTimeout(patientInfoCloseTimer.current);
    setPatientInfoHover(true);
  };

  const handlePatientInfoMouseLeave = () => {
    if (!patientInfoPinned) {
      patientInfoCloseTimer.current = setTimeout(() => setPatientInfoHover(false), 100);
    }
  };

  const handlePatientInfoClickAway = () => {
    clearTimeout(patientInfoCloseTimer.current);
    if (patientInfoPinned) {
      setPatientInfoPinned(false);
      setPatientInfoHover(false);
    }
  };

  const handleInfoIconClick = (e) => {
    e.stopPropagation();
    clearTimeout(patientInfoCloseTimer.current);

    if (patientInfoPinned) {
      setPatientInfoPinned(false);
      setPatientInfoHover(false);
    } else {
      setPatientInfoPinned(true);
      setPatientInfoHover(true);
    }
  };

  const handlePriorsClick = (study_id) => {
    dispatch(movePriorExaminations([study_id], t));
    dispatch(
      setSnackbar({
        msg: 'ResultView.pulling_from_pacs',
        severity: 'info',
      }),
    );
  };

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        margin: '0 4px',
        maxWidth: '100%',
      }}>
      <Tooltip
        title={
          <Typography className={classes.tooltip_texts}>
            {props.titlePrevious ? props.titlePrevious : 'N/A'}
          </Typography>
        }
        arrow
        interactive
        placement='top'
        TransitionComponent={Zoom}>
        <span>
          <IconButton
            onClick={props.onClickBackward}
            size='small'
            className={classes.next_icon}
            disabled={totalExaminations <= 1}>
            <NavigateBeforeIcon
              className={zoomedProjection ? classes.arrow_light : classes.arrow}
              fontSize='large'
            />
          </IconButton>
        </span>
      </Tooltip>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexDirection: 'row',
        }}>
        {results?.priors_found && (
          <>
            <Tooltip
              onOpen={() => setPriorHover(true)}
              onClose={() => setPriorHover(false)}
              title={
                <>
                  <Typography className={classes.tooltip_texts}>{t('Table.some_priors')}</Typography>
                  <Divider style={{ margin: '3%' }} />
                  <Typography className={classes.tooltip_texts}>
                    <TouchAppIcon fontSize='small' />
                    {t('Table.move_all_priors')}
                  </Typography>
                </>
              }>
              <IconButton
                aria-label='priors_row'
                size='small'
                onClick={() => handlePriorsClick(results.exam_id)}
                className={classes.hoverEffect}>
                {priorHover ? (
                  <GetAppSharpIcon fontSize='small' style={{ width: '18px' }} />
                ) : (
                  <RepeatIcon
                    fontSize='small'
                    style={{
                      width: '18px',
                      fill: '#dddddd',
                    }}
                  />
                )}
              </IconButton>
            </Tooltip>
          </>
        )}

        {results.pat_name && (
          <ClickAwayListener onClickAway={handlePatientInfoClickAway}>
            <div>
              <Tooltip
                arrow
                interactive
                placement='right-start'
                open={patientInfoOpen}
                disableHoverListener
                disableFocusListener
                PopperProps={{
                  onMouseEnter: handlePatientInfoMouseEnter,
                  onMouseLeave: handlePatientInfoMouseLeave,
                }}
                title={
                  <div
                    style={{
                      padding: '3%',
                      minWidth: '300px',
                      borderRadius: '5px',
                    }}>
                    <PatientSummaryTooltip results={results} />
                  </div>
                }>
                <div
                  onMouseEnter={handlePatientInfoMouseEnter}
                  onMouseLeave={handlePatientInfoMouseLeave}
                  onClick={handleInfoIconClick}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                  }}>
                  <div className={classes.pat_name_title_container}>
                    <Typography
                      className={patientInfoHover ? classes.pat_name_title_hovered : classes.pat_name_title}>
                      {results.pat_name.slice(0, 30)}
                    </Typography>
                  </div>

                  <InfoIcon
                    fontSize='small'
                    className={classes.patient_info_icon}
                    color={patientInfoHover || patientInfoPinned ? 'primary' : 'inherit'}
                  />
                </div>
              </Tooltip>
            </div>
          </ClickAwayListener>
        )}
      </div>

      <Tooltip
        title={
          <Typography className={classes.tooltip_texts}>
            {props.titleNext ? props.titleNext : 'N/A'}
          </Typography>
        }
        arrow
        interactive
        placement='top'
        TransitionComponent={Zoom}>
        <span>
          <IconButton
            onClick={props.onClickForward}
            size='small'
            className={classes.next_icon}
            disabled={totalExaminations <= 1}>
            <NavigateNextIcon
              className={zoomedProjection ? classes.arrow_light : classes.arrow}
              fontSize='large'
            />
          </IconButton>
        </span>
      </Tooltip>
    </div>
  );
}
